import React, { useState } from 'react';
import { STUDY_CATEGORIES, SUBJECTS, LANGUAGES, COUNTRIES, CITY_SUGGESTIONS } from '../constants';
import { zoneCity } from '../dateUtils';
import AvailabilityGrid from './AvailabilityGrid';
import TimezoneSelect from './TimezoneSelect';
import { Icon } from './ui';
import { toast } from './feedback';
import { MONTH_NAMES, isExamMonth, examYears, withMonth, withYear } from '../examMonth';

// The profile fields in four groups (About you · Exam & subjects · Languages · Your
// week), shared by the first-run setup and Profile's edit sheets (design F17).
export const STATUSES = [['pre_med', 'Pre-Med Student'], ['medical_student', 'Medical Student'], ['graduate', 'Medical graduate (not yet in residency)'], ['resident', 'Resident'], ['doctor', 'Doctor']];
const YEARS = [['Pre-Med Year 1'], ['Pre-Med Year 2'], ['Pre-Med Year 3'], ['Pre-Med Year 4'], ['MS1'], ['MS2'], ['MS3'], ['MS4'], ['Graduate', 'Graduate (not in residency)'], ['Intern', 'Intern (PGY-1)'], ['Resident', 'Resident (PGY-2+)'], ['Fellow'], ['Attending', 'Attending Physician']];

export const FieldError = ({ msg }) => (msg ? <div className="field-error" role="alert">{msg}</div> : null);

// p: the profile being edited; set(key, value); setCountry(country) also clears the city.
function ProfileFields({ step, p, set, setCountry, errors = {}, lead = true }) {
  const [tzOpen, setTzOpen] = useState(false);
  const [langQuery, setLangQuery] = useState('');
  const toggle = (k, item) => set(k, p[k].includes(item) ? p[k].filter(i => i !== item) : [...p[k], item]);
  const field = (label, k, input) => (
    <div className={`form-group ${errors[k] ? 'has-error' : ''}`}>
      <label htmlFor={`ob-${k}`}>{label}</label>
      {input}
      <FieldError msg={errors[k]} />
    </div>
  );
  const countryOptions = p.country && !COUNTRIES.includes(p.country) ? [p.country, ...COUNTRIES] : COUNTRIES;
  const languages = LANGUAGES.filter(l => l.toLowerCase().includes(langQuery.trim().toLowerCase()));

  return (
    <>
        {step === 0 && (
          <>
            {lead && <p className="muted onboarding-lead">This is what study partners see on your match card.</p>}
            {field('Your name', 'name', <input id="ob-name" type="text" value={p.name} onChange={e => set('name', e.target.value)} autoComplete="name" />)}
            <div className="grid-2">
              {field('Age', 'age', <input id="ob-age" type="number" inputMode="numeric" min="18" max="100" value={p.age} onChange={e => set('age', e.target.value)} />)}
              {field('Gender', 'gender', <select id="ob-gender" value={p.gender} onChange={e => set('gender', e.target.value)}><option value="">Select</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option></select>)}
            </div>
            <div className="grid-2">
              {field('Status', 'status', <select id="ob-status" value={p.status} onChange={e => set('status', e.target.value)}>{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>)}
              {field('Year', 'yearLevel', <select id="ob-yearLevel" value={p.yearLevel} onChange={e => set('yearLevel', e.target.value)}><option value="">Select year</option>{YEARS.map(([v, l]) => <option key={v} value={v}>{l || v}</option>)}</select>)}
            </div>
            {field('School or hospital', 'school', <input id="ob-school" type="text" value={p.school} onChange={e => set('school', e.target.value)} />)}
            <div className="grid-2">
              {field('Country', 'country', <select id="ob-country" value={p.country} onChange={e => setCountry(e.target.value)}><option value="">Select country</option>{countryOptions.map(c => <option key={c}>{c}</option>)}</select>)}
              {field('City', 'city', <><input id="ob-city" type="text" list="ob-city-list" value={p.city} onChange={e => set('city', e.target.value)} /><datalist id="ob-city-list">{(CITY_SUGGESTIONS[p.country] || []).map(c => <option key={c} value={c} />)}</datalist></>)}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="section-label">I'm studying for</div>
            <div className="study-category-grid">
              {STUDY_CATEGORIES.map(cat => (
                <button key={cat} type="button" onClick={() => set('studyCategory', cat)} aria-pressed={p.studyCategory === cat} className={`study-category-card ${p.studyCategory === cat ? 'selected' : ''}`}>
                  {p.studyCategory === cat && <span className="category-check"><Icon name="check" size={18} /></span>}
                  <div className="category-label">{cat}</div>
                </button>
              ))}
            </div>
            <FieldError msg={errors.studyCategory} />
            {/* Optional (P-2): when they plan to sit the exam. */}
            <div className="grid-2 exam-month">
              <div className="form-group">
                <label htmlFor="ob-examMonth">Planned exam month (optional)</label>
                <select id="ob-examMonth" value={isExamMonth(p.examMonth) ? String(Number(p.examMonth.slice(5))) : ''} onChange={e => set('examMonth', withMonth(p.examMonth, e.target.value))}>
                  <option value="">Not decided yet</option>
                  {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="ob-examYear">Year</label>
                <select id="ob-examYear" value={isExamMonth(p.examMonth) ? p.examMonth.slice(0, 4) : ''} disabled={!isExamMonth(p.examMonth)} onChange={e => set('examMonth', withYear(p.examMonth, e.target.value))}>
                  {!isExamMonth(p.examMonth) && <option value="">—</option>}
                  {[...new Set([...examYears(), ...(isExamMonth(p.examMonth) ? [Number(p.examMonth.slice(0, 4))] : [])])].sort().map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <SubjectPicker label="I can teach" field="strengths" other="wantsToLearn" otherLabel="Want to learn" p={p} set={set} />
            <FieldError msg={errors.strengths} />
            <SubjectPicker label="I want to learn" field="wantsToLearn" other="strengths" otherLabel="I can teach" p={p} set={set} />
            <FieldError msg={errors.wantsToLearn} />
          </>
        )}

        {step === 2 && (
          <>
            <p className="muted onboarding-lead">Partners who share a language other than English show up first.</p>
            <div className="chip-picker selected-langs" aria-label="Chosen languages">
              {p.languages.map(l => (
                <button key={l} type="button" className="chip accent removable" onClick={() => toggle('languages', l)} aria-label={`Remove ${l}`}>{l}<Icon name="close" size={14} /></button>
              ))}
            </div>
            <FieldError msg={errors.languages} />
            <div className="form-group">
              <label htmlFor="ob-lang">Add a language</label>
              <input id="ob-lang" type="search" placeholder="Search languages" value={langQuery} onChange={e => setLangQuery(e.target.value)} />
            </div>
            <div className="lang-list">
              {languages.filter(l => !p.languages.includes(l)).map(l => (
                <button key={l} type="button" className="lang-row" onClick={() => { toggle('languages', l); setLangQuery(''); }}>{l}<Icon name="check" size={18} /></button>
              ))}
              {languages.length === 0 && <p className="small muted">No language matches “{langQuery}”.</p>}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p className="tz-line">
              Shown in your time ({zoneCity(p.timezone)}). Partners see these hours in theirs.{' '}
              <button type="button" className="btn-text" onClick={() => setTzOpen(o => !o)}>{tzOpen ? 'Done' : 'Change'}</button>
            </p>
            {tzOpen && (
              <div className="form-group">
                <label htmlFor="ob-tz">Your timezone</label>
                <TimezoneSelect id="ob-tz" value={p.timezone} onChange={v => set('timezone', v)} />
              </div>
            )}
            <FieldError msg={errors.availabilityLocal} />
            <AvailabilityGrid value={p.availabilityLocal} onChange={list => set('availabilityLocal', list)} />
          </>
        )}

    </>
  );
}
export default ProfileFields;

// One subject list (review R3). A subject in the other list is greyed, not struck
// through; tapping it moves it here, so a subject is never in both lists.
function SubjectPicker({ label, field, other, otherLabel, p, set }) {
  const tap = s => {
    if (p[field].includes(s)) { set(field, p[field].filter(x => x !== s)); return; }
    if (p[other].includes(s)) {
      set(other, p[other].filter(x => x !== s));
      toast(`Moved ${s} to ${label}`);
    }
    set(field, [...p[field], s]);
  };
  const anyTaken = SUBJECTS.some(s => p[other].includes(s) && !p[field].includes(s));
  return (
    <>
      <div className="section-label">{label}</div>
      {anyTaken && <p className="small muted subject-hint">Greyed subjects are in {otherLabel}. Tap one to move it here.</p>}
      <div className="chip-picker" role="group" aria-label={label}>
        {SUBJECTS.map(s => {
          const on = p[field].includes(s);
          const taken = !on && p[other].includes(s);
          return <button key={s} type="button" className={`pick-chip ${on ? 'on' : ''} ${taken ? 'taken' : ''}`} aria-pressed={on} title={taken ? `In ${otherLabel}. Tap to move it here.` : undefined} onClick={() => tap(s)}>{s}</button>;
        })}
      </div>
    </>
  );
}

